import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import {
  QuestionCombobox,
  TEMPLATE_CONDITIONAL_OPERATORS,
  TEMPLATE_CONDITIONAL_LOGIC,
} from '@/features/quote-and-apply-builder';

import { Plus, X } from 'lucide-react';

interface TemplateConditionBuilderProps {
  conditional: TemplateConditional;
  questions: Record<string, PolicyFormQuestion>;
  onAddRule: () => void;
  onUpdateRule: (index: number, updates: Partial<TemplateConditionalRule>) => void;
  onRemoveRule: (index: number) => void;
  onSetLogic: (logic: TemplateConditional['logic']) => void;
}

const VALUE_HIDDEN_OPERATORS = new Set(['empty', 'notEmpty']);

function TemplateConditionRow({
  rule,
  index,
  questions,
  questionOptions,
  onUpdate,
  onRemove,
}: {
  rule: TemplateConditionalRule;
  index: number;
  questions: Record<string, PolicyFormQuestion>;
  questionOptions: {
    value: string;
    label: string;
    selectedDisplay: string;
    optionDisplay: React.ReactNode;
  }[];
  onUpdate: (index: number, updates: Partial<TemplateConditionalRule>) => void;
  onRemove: (index: number) => void;
}) {
  const selectedQuestion = questions[rule.field];
  const showValueInput = !VALUE_HIDDEN_OPERATORS.has(rule.operator);

  return (
    <div className="flex w-full flex-col gap-2 overflow-hidden rounded-xl border p-2">
      <div className="grid grid-cols-2 gap-2">
        <QuestionCombobox
          value={rule.field}
          options={questionOptions}
          onChange={val => onUpdate(index, { field: val as string, value: '' })}
          className="w-full"
        />

        <Select
          value={rule.operator}
          onValueChange={op =>
            onUpdate(index, { operator: op as TemplateConditionalRule['operator'] })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEMPLATE_CONDITIONAL_OPERATORS.map(op => (
              <SelectItem
                key={op.value}
                value={op.value}
              >
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex w-full items-center gap-2">
        {showValueInput &&
          (selectedQuestion?.type === 'select' || selectedQuestion?.type === 'radio' ? (
            <Select
              value={String(rule.value ?? '')}
              onValueChange={val => onUpdate(index, { value: val })}
            >
              <SelectTrigger className="w-full truncate">
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                {selectedQuestion.options?.map(opt => (
                  <SelectItem
                    key={opt.id}
                    value={opt.id}
                  >
                    {opt.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={String(rule.value ?? '')}
              onChange={e => onUpdate(index, { value: e.target.value })}
              placeholder="Value"
              className="w-full"
            />
          ))}

        <Button
          variant="outline"
          className="shrink-0"
          onClick={() => onRemove(index)}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

export function TemplateConditionBuilder({
  conditional,
  questions,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onSetLogic,
}: TemplateConditionBuilderProps) {
  const questionOptions = Object.values(questions).map(question => ({
    value: question.questionRef,
    label: question.questionRef,
    selectedDisplay: question.questionRef,
    optionDisplay: (
      <div className="flex w-64 flex-col gap-1">
        <span className="text-sm font-medium text-gray-950">{question.questionRef}</span>
        <span className="text-foreground truncate text-xs">{question.title}</span>
      </div>
    ),
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Inclusion Conditions</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAddRule}
        >
          <Plus />
          Add Rule
        </Button>
      </div>

      {conditional.rules.length === 0 && (
        <p className="text-muted-foreground text-sm">
          No conditions — this template will always be included in the envelope.
        </p>
      )}

      {conditional.rules.length > 1 && (
        <div className="flex flex-row gap-2">
          <Label className="text-muted-foreground text-xs">Logic:</Label>
          <Select
            value={conditional.logic}
            onValueChange={value => onSetLogic(value as TemplateConditional['logic'])}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATE_CONDITIONAL_LOGIC.map(option => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid space-y-2">
        {conditional.rules.map((rule, index) => (
          <TemplateConditionRow
            key={index}
            rule={rule}
            index={index}
            questions={questions}
            questionOptions={questionOptions}
            onUpdate={onUpdateRule}
            onRemove={onRemoveRule}
          />
        ))}
      </div>
    </div>
  );
}
